import React, { useState } from "react";
import { api, unwrap, errMsg } from "@/lib/api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Sparkles, ImageIcon, Plus, Download, FileWarning, Upload } from "lucide-react";
import { formatDate } from "@/lib/utils";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/i18n";
import { levelDisplayName } from "@/lib/levelLabels";
import { useLookupValues } from "@/lib/platformConfig";
import { DatePicker } from "@/components/ui/calendar";

const ASSESS_CLASS = {
  emerging: "bg-[var(--subtle-bg)] text-[var(--text-muted)] border-0",
  developing: "bg-[var(--info-bg)] text-[var(--info)] border-0",
  secure: "bg-[var(--success-bg)] text-[var(--success)] border-0",
  exceeding: "bg-[var(--accent-bg)] text-[var(--accent)] border-0",
  not_assessed: "bg-[var(--subtle-bg)] text-[var(--text-muted)] border-0",
};

const isReportRangeValid = (range) =>
  Boolean(
    range.start_date && range.end_date && range.start_date <= range.end_date,
  );

export default function Nursery() {
  const { t } = useI18n();
  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="nursery-page">
      <div className="mb-6">
        <h1 className="font-display text-3xl font-bold text-[var(--text-primary)]">
          {t("nursery.pageTitle")}
        </h1>
        <p className="text-sm text-[var(--text-muted)] mt-1">
          {t("nursery.pageSubtitle")}
        </p>
      </div>
      <Tabs defaultValue="eyfs">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="eyfs" data-testid="tab-eyfs">
            <Sparkles className="h-3.5 w-3.5 mr-1.5" /> {t("nursery.tabEyfs")}
          </TabsTrigger>
          <TabsTrigger value="gallery" data-testid="tab-gallery">
            <ImageIcon className="h-3.5 w-3.5 mr-1.5" />{" "}
            {t("nursery.tabGallery")}
          </TabsTrigger>
          <TabsTrigger value="docs" data-testid="tab-docs">
            <FileWarning className="h-3.5 w-3.5 mr-1.5" />{" "}
            {t("nursery.tabDocs")}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="eyfs">
          <EYFSTab />
        </TabsContent>
        <TabsContent value="gallery">
          <GalleryTab />
        </TabsContent>
        <TabsContent value="docs">
          <DocsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function EYFSTab() {
  const { t, language } = useI18n();
  const qc = useQueryClient();
  const { data: areas } = useQuery({
    queryKey: ["eyfs-areas"],
    queryFn: async () => unwrap(await api.get("/eyfs/learning-areas")),
  });
  const assessmentLookup = useLookupValues("eyfs_assessment_levels");
  const { data: obs } = useQuery({
    queryKey: ["eyfs-obs"],
    queryFn: async () => unwrap(await api.get("/eyfs/observations")),
  });
  const { data: students } = useQuery({
    queryKey: ["students-lite"],
    queryFn: async () => unwrap(await api.get("/students?page_size=100")),
  });
  const [portfolioStudentId, setPortfolioStudentId] = useState("");
  const [reportRange, setReportRange] = useState({
    start_date: "",
    end_date: "",
  });
  const [reportDownloading, setReportDownloading] = useState(false);
  const {
    data: portfolio,
    isLoading: portfolioLoading,
    isError: portfolioError,
    error: portfolioErrorDetail,
  } = useQuery({
    queryKey: ["eyfs-portfolio", portfolioStudentId],
    queryFn: async () =>
      unwrap(await api.get(`/eyfs/portfolio/${portfolioStudentId}`)),
    enabled: !!portfolioStudentId,
  });
  const [show, setShow] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    student_id: "",
    learning_area_code: "",
    milestone: "",
    description: "",
    assessment: "",
    observation_date: today,
  });
  const assessmentOptions = (assessmentLookup.data?.values || []).map(
    (row) => ({ value: row.value, label: row.label }),
  );

  const create = useMutation({
    mutationFn: async () => unwrap(await api.post("/eyfs/observations", form)),
    onSuccess: () => {
      toast.success(t("nursery.observationLoggedToast"));
      setShow(false);
      setForm({ ...form, milestone: "", description: "" });
      qc.invalidateQueries({ queryKey: ["eyfs-obs"] });
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const studentsList =
    students?.items ||
    students?.data ||
    (Array.isArray(students) ? students : []);
  const assessmentLabel = (level) =>
    ({
      emerging: t("nursery.levelEmerging"),
      developing: t("nursery.levelDeveloping"),
      secure: t("nursery.levelSecure"),
      exceeding: t("nursery.levelExceeding"),
      not_assessed: t("nursery.assessment"),
    })[level] || level;

  const downloadReport = async () => {
    if (!portfolioStudentId || !isReportRangeValid(reportRange)) return;
    setReportDownloading(true);
    try {
      const response = await api.get(
        `/eyfs/termly-report.pdf/${portfolioStudentId}`,
        { params: { lang: language, ...reportRange }, responseType: "blob" },
      );
      const url = window.URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" }),
      );
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `eyfs-termly-${portfolio?.student?.admission_no || portfolioStudentId}.pdf`;
      anchor.click();
      window.URL.revokeObjectURL(url);
      toast.success(t("nursery.reportDownloadedToast"));
    } catch (e) {
      toast.error(t("nursery.reportDownloadError"));
    } finally {
      setReportDownloading(false);
    }
  };

  return (
    <div className="mt-6">
      <Card className="border-[var(--subtle)]">
        <CardHeader className="flex-row flex-wrap gap-2 items-center justify-between">
          <CardTitle className="text-sm font-display">
            {t("nursery.recentObservations", { count: (obs || []).length })}
          </CardTitle>
          <Dialog open={show} onOpenChange={setShow}>
            <DialogTrigger asChild>
              <Button
                size="sm"
                className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white"
                data-testid="new-obs-btn"
              >
                <Plus className="h-4 w-4 mr-1.5" />{" "}
                {t("nursery.newObservation")}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t("nursery.logObservation")}</DialogTitle>
                <DialogDescription>
                  {t("nursery.pageSubtitle")}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <Select
                  value={form.student_id}
                  onValueChange={(v) => setForm({ ...form, student_id: v })}
                >
                  <SelectTrigger data-testid="obs-student">
                    <SelectValue placeholder={t("nursery.selectChild")} />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {studentsList.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.first_name} {s.last_name} · {s.admission_no}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="grid grid-cols-2 gap-3">
                  <Select
                    value={form.learning_area_code}
                    onValueChange={(v) =>
                      setForm({ ...form, learning_area_code: v })
                    }
                  >
                    <SelectTrigger data-testid="obs-area">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(areas || []).map((a) => (
                        <SelectItem key={a.code} value={a.code}>
                          {a.code} · {a.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select
                    value={form.assessment}
                    onValueChange={(v) => setForm({ ...form, assessment: v })}
                  >
                    <SelectTrigger data-testid="obs-level">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {assessmentOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Input
                  placeholder={t("nursery.milestonePlaceholder")}
                  value={form.milestone}
                  onChange={(e) =>
                    setForm({ ...form, milestone: e.target.value })
                  }
                  data-testid="obs-milestone"
                />
                <Textarea
                  placeholder={t("nursery.observationPlaceholder")}
                  value={form.description}
                  onChange={(e) =>
                    setForm({ ...form, description: e.target.value })
                  }
                  data-testid="obs-desc"
                />
                <DatePicker
                  value={form.observation_date}
                  onChange={(date) =>
                    setForm({ ...form, observation_date: date })
                  }
                  testId="obs-date"
                />
              </div>
              <DialogFooter>
                <Button
                  onClick={() => create.mutate()}
                  disabled={
                    !form.student_id || !form.description || create.isPending
                  }
                  data-testid="obs-save"
                >
                  {t("nursery.save")}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm grid-table">
              <thead>
                <tr>
                  <th className="text-left px-3 py-3">{t("nursery.thDate")}</th>
                  <th className="text-left px-3 py-3">
                    {t("nursery.thChild")}
                  </th>
                  <th className="text-left px-3 py-3">{t("nursery.thArea")}</th>
                  <th className="text-left px-3 py-3">
                    {t("nursery.thMilestone")}
                  </th>
                  <th className="text-left px-3 py-3">
                    {t("nursery.thAssessment")}
                  </th>
                  <th className="text-left px-3 py-3">{t("nursery.thNote")}</th>
                </tr>
              </thead>
              <tbody>
                {(obs || []).map((o) => (
                  <tr key={o.id} data-testid={`obs-${o.id}`}>
                    <td className="px-3 py-3 font-mono-num text-xs">
                      {formatDate(o.observation_date)}
                    </td>
                    <td className="px-3 py-3 font-medium">{o.student_name}</td>
                    <td className="px-3 py-3">
                      <Badge className="bg-[var(--subtle-bg)] text-[var(--text-secondary)] border-0 text-xs">
                        {o.learning_area_code}
                      </Badge>
                    </td>
                    <td className="px-3 py-3 text-xs">{o.milestone || "—"}</td>
                    <td className="px-3 py-3">
                      <Badge
                        className={
                          ASSESS_CLASS[o.assessment] ||
                          ASSESS_CLASS.not_assessed
                        }
                      >
                        {o.assessment}
                      </Badge>
                    </td>
                    <td className="px-3 py-3 text-xs max-w-md text-[var(--text-secondary)]">
                      {o.description}
                    </td>
                  </tr>
                ))}
                {(obs || []).length === 0 && (
                  <tr>
                    <td
                      colSpan={6}
                      className="py-8 text-center text-[var(--text-muted)]"
                    >
                      {t("nursery.noObservationsYet")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
      <Card
        className="border-[var(--subtle)] mt-4"
        data-testid="eyfs-portfolio"
      >
        <CardHeader className="flex-row flex-wrap gap-2 items-center justify-between">
          <CardTitle className="text-sm font-display">
            {t("nursery.tabEyfs")}
          </CardTitle>
          <div className="flex flex-wrap gap-2 w-full sm:w-auto">
            <Select
              value={portfolioStudentId}
              onValueChange={setPortfolioStudentId}
            >
              <SelectTrigger
                className="w-full sm:w-72"
                data-testid="portfolio-student"
              >
                <SelectValue placeholder={t("nursery.selectChild")} />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {studentsList.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.first_name} {s.last_name} · {s.admission_no}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <fieldset
              className="grid grid-cols-[1fr_auto_1fr] items-center gap-1 w-full sm:w-auto"
              data-testid="eyfs-report-period"
            >
              <legend className="sr-only">{t("finance.period")}</legend>
              <DatePicker
                ariaLabel={t("finance.period")}
                value={reportRange.start_date}
                max={reportRange.end_date || undefined}
                onChange={(date) =>
                  setReportRange({ ...reportRange, start_date: date })
                }
                testId="eyfs-report-start"
              />
              <span
                aria-hidden="true"
                className="text-xs text-[var(--text-muted)]"
              >
                →
              </span>
              <DatePicker
                ariaLabel={t("finance.period")}
                value={reportRange.end_date}
                min={reportRange.start_date || undefined}
                onChange={(date) =>
                  setReportRange({ ...reportRange, end_date: date })
                }
                testId="eyfs-report-end"
              />
            </fieldset>
            <Button
              variant="outline"
              size="sm"
              onClick={downloadReport}
              disabled={
                !portfolio ||
                !isReportRangeValid(reportRange) ||
                reportDownloading
              }
              data-testid="download-eyfs-report"
              title={t("nursery.downloadTermlyReport")}
            >
              <Download className="h-3.5 w-3.5 mr-1.5" />{" "}
              {t("nursery.downloadTermlyReport")}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {portfolioLoading && (
            <div
              className="text-sm text-[var(--text-muted)]"
              data-testid="portfolio-loading"
            >
              {t("nursery.recentObservations", { count: 0 })}
            </div>
          )}
          {portfolioError && (
            <div
              className="text-sm text-[var(--error)]"
              data-testid="portfolio-error"
            >
              {errMsg(portfolioErrorDetail)}
            </div>
          )}
          {!portfolioStudentId && !portfolioLoading && (
            <div className="text-sm text-[var(--text-muted)]">
              {t("nursery.selectChild")}
            </div>
          )}
          {portfolio && (
            <div data-testid="portfolio-records">
              <div className="flex flex-wrap items-baseline justify-between gap-2 mb-4">
                <div className="font-semibold text-[var(--text-primary)]">
                  {portfolio.student?.name}
                </div>
                <div className="text-xs text-[var(--text-muted)] font-mono-num">
                  {t("nursery.recentObservations", {
                    count: portfolio.total_observations,
                  })}
                </div>
              </div>
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                {(portfolio.areas || []).map((area) => (
                  <div
                    key={area.code}
                    className="border border-[var(--subtle)] rounded-lg p-3"
                    data-testid={`portfolio-area-${area.code}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="font-semibold text-sm">{area.name}</div>
                        <div className="text-xs text-[var(--text-muted)] mt-1">
                          {t("nursery.recentObservations", {
                            count: area.observations_count,
                          })}
                        </div>
                      </div>
                      <Badge
                        className={
                          ASSESS_CLASS[area.current_level] ||
                          ASSESS_CLASS.not_assessed
                        }
                      >
                        {assessmentLabel(area.current_level)}
                      </Badge>
                    </div>
                    <div className="mt-3 space-y-2">
                      {(area.recent || []).map((record) => (
                        <div
                          key={record.id}
                          className="border-t border-[var(--subtle)] pt-2 text-xs"
                        >
                          <div className="flex justify-between gap-2 text-[var(--text-muted)]">
                            <span>{formatDate(record.observation_date)}</span>
                            <span>{assessmentLabel(record.assessment)}</span>
                          </div>
                          <div className="mt-1 text-[var(--text-secondary)]">
                            {record.milestone || record.description}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function GalleryTab() {
  const { t, language } = useI18n();
  const { user } = useAuth();
  const qc = useQueryClient();
  const {
    data: albums,
    isLoading: albumsLoading,
    isError: albumsError,
    error: albumsErrorDetail,
  } = useQuery({
    queryKey: ["gallery-albums"],
    queryFn: async () => unwrap(await api.get("/gallery/albums")),
  });
  const teacherOnly =
    user?.roles?.includes("teacher") &&
    !["platform_admin", "tenant_owner", "principal", "administrator"].some(
      (role) => user.roles.includes(role),
    );
  const { data: teacherHome } = useQuery({
    queryKey: ["teacher-home"],
    queryFn: async () => unwrap(await api.get("/teacher/home")),
    enabled: teacherOnly,
  });
  const {
    data: galleryStudents,
    isLoading: galleryStudentsLoading,
    isError: galleryStudentsError,
  } = useQuery({
    queryKey: ["gallery-students"],
    queryFn: async () => unwrap(await api.get("/students?page_size=100")),
  });
  const sections = teacherHome?.sections || [];
  const today = new Date().toISOString().slice(0, 10);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({
    title: "",
    description: "",
    album_date: today,
    section_id: "",
  });
  const create = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.post(
          "/gallery/albums",
          teacherOnly
            ? form
            : {
                title: form.title,
                description: form.description,
                album_date: form.album_date,
              },
        ),
      ),
    onSuccess: () => {
      toast.success(t("nursery.albumCreatedToast"));
      setShow(false);
      setForm({ title: "", description: "", album_date: today, section_id: "" });
      qc.invalidateQueries({ queryKey: ["gallery-albums"] });
    },
    onError: (e) => toast.error(errMsg(e)),
  });
  return (
    <div className="mt-6">
      <Card className="border-[var(--subtle)]">
        <CardHeader className="flex-row flex-wrap gap-2 items-center justify-between">
          {albumsLoading ? (
            <Skeleton
              className="h-5 w-24"
              data-testid="gallery-loading-title"
            />
          ) : (
            <CardTitle className="text-sm font-display">
              {t("nursery.albumsCount", { count: (albums || []).length })}
            </CardTitle>
          )}
          <Dialog open={show} onOpenChange={setShow}>
            <DialogTrigger asChild>
              <Button
                size="sm"
                className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white"
                data-testid="new-album-btn"
              >
                <Plus className="h-4 w-4 mr-1.5" /> {t("nursery.newAlbum")}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t("nursery.createAlbum")}</DialogTitle>
                <DialogDescription className="sr-only">
                  {t("nursery.createAlbum")}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                {teacherOnly && (
                  <Select
                    value={form.section_id}
                    onValueChange={(section_id) =>
                      setForm({ ...form, section_id })
                    }
                  >
                    <SelectTrigger data-testid="album-section">
                      <SelectValue placeholder={t("teacher.chooseSection")} />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      {sections.map((section) => (
                        <SelectItem key={section.id} value={section.id}>
                          {levelDisplayName(section.grade, language)} · {section.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <Input
                  placeholder={t("teacher.title")}
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  data-testid="album-title"
                />
                <Textarea
                  placeholder={t("nursery.description")}
                  value={form.description}
                  onChange={(e) =>
                    setForm({ ...form, description: e.target.value })
                  }
                  data-testid="album-desc"
                />
                <div className="space-y-1.5">
                  <div className="text-xs font-medium text-[var(--text-secondary)]">
                    {t("nursery.thDate")}
                  </div>
                  <DatePicker
                    value={form.album_date}
                    onChange={(album_date) => setForm({ ...form, album_date })}
                    ariaLabel={t("nursery.thDate")}
                    testId="album-date"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  onClick={() => create.mutate()}
                  disabled={
                    !form.title ||
                    !form.album_date ||
                    (teacherOnly && !form.section_id) ||
                    create.isPending
                  }
                  data-testid="album-save"
                >
                  {t("nursery.create")}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent className="grid md:grid-cols-3 gap-3">
          {albumsError && (
            <div
              className="col-span-3 text-sm text-[var(--error)]"
              role="alert"
              data-testid="gallery-error"
            >
              {errMsg(albumsErrorDetail)}
            </div>
          )}
          {!albumsError &&
            albumsLoading &&
            Array.from({ length: 3 }, (_, index) => (
              <Skeleton
                key={index}
                className="h-28"
                data-testid="gallery-loading"
              />
            ))}
          {!albumsError &&
            (albums || []).map((a) => (
              <div
                key={a.id}
                className="border border-[var(--subtle)] rounded-lg p-4"
                data-testid={`album-${a.id}`}
              >
                <div className="font-semibold text-sm break-words">
                  {a.title}
                </div>
                <div className="text-xs text-[var(--text-muted)] mt-1">
                  {a.description || "—"}
                </div>
                <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                  <div className="space-y-1">
                    <div className="text-xs text-[var(--text-muted)] font-mono-num">
                      {formatDate(a.album_date || a.created_at, language)}
                    </div>
                    <div className="text-xs text-[var(--accent)] font-mono-num">
                      {t("nursery.mediaCount", { count: a.media_count || 0 })}
                    </div>
                  </div>
                  <AlbumPhotoUpload
                    album={a}
                    students={galleryStudents || []}
                    studentsLoading={galleryStudentsLoading}
                    studentsError={galleryStudentsError}
                    onUploaded={() => qc.invalidateQueries({ queryKey: ["gallery-albums"] })}
                    t={t}
                  />
                </div>
              </div>
            ))}
          {!albumsError && !albumsLoading && (albums || []).length === 0 && (
            <div className="col-span-3 text-center text-sm text-[var(--text-muted)] py-8">
              {t("nursery.noAlbumsYet")}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function AlbumPhotoUpload({ album, students, studentsLoading, studentsError, onUploaded, t }) {
  const [open, setOpen] = useState(false);
  const [files, setFiles] = useState([]);
  const [studentIds, setStudentIds] = useState([]);
  const [inputKey, setInputKey] = useState(0);

  const upload = useMutation({
    mutationFn: async () => {
      for (const file of files) {
        const body = new FormData();
        body.append("file", file);
        body.append("is_sensitive", "true");
        const stored = unwrap(await api.post("/files/upload", body, {
          headers: { "Content-Type": "multipart/form-data" },
        }));
        await api.post("/gallery/media", {
          album_id: album.id,
          kind: "photo",
          file_id: stored.id,
          caption: file.name,
          tagged_student_ids: studentIds,
        });
      }
      return files.length;
    },
    onSuccess: (count) => {
      toast.success(t("nursery.mediaCount", { count }));
      setFiles([]);
      setStudentIds([]);
      setInputKey((value) => value + 1);
      setOpen(false);
      onUploaded();
    },
    onError: (error) => toast.error(errMsg(error)),
  });

  const toggleStudent = (studentId, checked) => {
    setStudentIds((current) => checked
      ? [...new Set([...current, studentId])]
      : current.filter((id) => id !== studentId));
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-8"
          data-testid={`album-upload-${album.id}`}
        >
          <Upload className="h-3.5 w-3.5 me-1.5" />
          {t("admissionWizard.documents.upload")}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{album.title}</DialogTitle>
          <DialogDescription>{album.description || t("nursery.description")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <div className="text-xs font-medium text-[var(--text-secondary)]">
              {t("admissionWizard.documents.upload")}
            </div>
            <Input
              key={inputKey}
              type="file"
              accept="image/jpeg,image/png"
              multiple
              onChange={(event) => setFiles(Array.from(event.target.files || []))}
              data-testid={`album-photo-input-${album.id}`}
            />
            {!!files.length && (
              <div className="text-xs text-[var(--text-muted)] break-words">
                {files.map((file) => file.name).join(", ")}
              </div>
            )}
          </div>
          <fieldset className="space-y-2">
            <legend className="text-xs font-medium text-[var(--text-secondary)]">
              {t("nursery.selectChild")}
            </legend>
            <div className="max-h-48 overflow-y-auto rounded-md border border-[var(--subtle)] p-3 space-y-2">
              {studentsLoading && <Skeleton className="h-16 w-full" />}
              {studentsError && (
                <div className="text-xs text-[var(--error)]" role="alert">
                  {t("admissionWizard.loadError")}
                </div>
              )}
              {!studentsLoading && !studentsError && students.map((student) => {
                const name = `${student.first_name || ""} ${student.last_name || ""}`.trim() || student.id;
                return (
                  <label key={student.id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox
                      checked={studentIds.includes(student.id)}
                      onCheckedChange={(checked) => toggleStudent(student.id, checked === true)}
                      data-testid={`album-child-${student.id}`}
                    />
                    <span className="min-w-0 break-words">{name}</span>
                  </label>
                );
              })}
            </div>
          </fieldset>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={() => upload.mutate()}
            disabled={!files.length || !studentIds.length || upload.isPending}
            data-testid={`album-photo-save-${album.id}`}
          >
            {upload.isPending ? t("common.saving") : t("nursery.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DocsTab() {
  const { t } = useI18n();
  const { data } = useQuery({
    queryKey: ["docs-expiring"],
    queryFn: async () => unwrap(await api.get("/documents/expiring?days=90")),
  });
  const buckets = data?.buckets || {};
  const groups = [
    { key: "expired", label: t("nursery.expired"), accent: "error" },
    { key: "30d", label: t("nursery.d30"), accent: "error" },
    { key: "60d", label: t("nursery.d60"), accent: "warning" },
    { key: "90d", label: t("nursery.d90"), accent: "info" },
  ];
  return (
    <div className="mt-6 space-y-4">
      <div className="grid md:grid-cols-4 gap-3">
        {groups.map((g) => (
          <Card
            key={g.key}
            className={`border-[var(--subtle)] border-l-4 border-l-[var(--${g.accent === "error" ? "error" : g.accent === "warning" ? "warning" : "info"})]`}
            data-testid={`bucket-${g.key}`}
          >
            <CardContent className="p-4">
              <div className="text-xs text-[var(--text-muted)] uppercase tracking-wider">
                {g.label}
              </div>
              <div className="text-3xl font-display font-bold mt-1 font-mono-num">
                {(buckets[g.key] || []).length}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      <Card className="border-[var(--subtle)]">
        <CardHeader>
          <CardTitle className="text-sm font-display">
            {t("nursery.expiring90Title")}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm grid-table">
              <thead>
                <tr>
                  <th className="text-left px-3 py-3">
                    {t("nursery.thBucket")}
                  </th>
                  <th className="text-left px-3 py-3">
                    {t("nursery.thPerson")}
                  </th>
                  <th className="text-left px-3 py-3">{t("nursery.thType")}</th>
                  <th className="text-left px-3 py-3">{t("nursery.thKind")}</th>
                  <th className="text-left px-3 py-3">
                    {t("nursery.thNumber")}
                  </th>
                  <th className="text-left px-3 py-3">
                    {t("nursery.thExpires")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {groups.flatMap((g) =>
                  (buckets[g.key] || []).map((d) => (
                    <tr key={d.id} data-testid={`doc-${d.id}`}>
                      <td className="px-3 py-3">
                        <Badge
                          className={`bg-[var(--${g.accent}-bg)] text-[var(--${g.accent})] border-0 text-xs`}
                        >
                          {g.label}
                        </Badge>
                      </td>
                      <td className="px-3 py-3 font-medium">
                        {d.subject_name || "—"}
                      </td>
                      <td className="px-3 py-3 text-xs text-[var(--text-muted)]">
                        {d.subject_type}
                      </td>
                      <td className="px-3 py-3 text-xs">{d.kind}</td>
                      <td className="px-3 py-3 font-mono-num text-xs">
                        {d.number || "—"}
                      </td>
                      <td className="px-3 py-3 font-mono-num text-xs">
                        {formatDate(d.expiry_date)}
                      </td>
                    </tr>
                  )),
                )}
                {(data?.total || 0) === 0 && (
                  <tr>
                    <td
                      colSpan={6}
                      className="py-8 text-center text-[var(--text-muted)]"
                    >
                      {t("nursery.noDocsExpiring")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
